import { useEffect, useRef } from 'react';
import { useAuth } from './useAuth';
import { shellEventBus } from '../services/shellBridge';
import type { Auth0State } from '@opengov/psp-contract';

/**
 * Broadcasts PSP shell auth state changes to the shell event bus so mounted
 * ES modules using `@opengov/shell-auth0-react` (or anything subscribed to
 * `shell.auth0.onAuth0StateChange`) stay in sync without remounting on token
 * refresh, login, or logout.
 *
 * Source of truth is `useAuth()`, which wraps `useAuth0()` from
 * `@auth0/auth0-react`. The values are passed through unchanged so the bridged
 * provider sees Auth0's own semantics (isLoading especially).
 *
 * Skips the initial mount to avoid emitting on page load — subscribers can
 * pull current state via `shell.auth0.getAuth0State()` if they need it before
 * the first change event fires.
 */
export function useAuth0Broadcast(): void {
  const { isAuthenticated, isLoading, user, error } = useAuth();
  const isMountRef = useRef(true);

  // Intentional use of useEffect (same justification as useNavigationBroadcast):
  //   - Cannot derive state: emission is a side effect, not a value.
  //   - Cannot use a data-fetching hook: this isn't a request.
  //   - Cannot emit from event handlers: Auth0 state changes come from the SDK
  //     itself (silent token refresh, session-revocation, cross-tab sync), not
  //     from user clicks we can hook.
  //   - useMountEffect doesn't apply: we must react to every state change.
  useEffect(() => {
    if (isMountRef.current) {
      isMountRef.current = false;
      return;
    }
    const state: Auth0State = {
      isAuthenticated,
      isLoading,
      user: user ? { ...user } : undefined,
      error,
    };
    // TODO(logger): when the shell exposes a logger via the bridge, log every
    // emission at debug level (redacting user.email/user.sub for prod sinks).
    shellEventBus.emit('auth0Change', state);
  }, [isAuthenticated, isLoading, user, error]);
}
